import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Upload, Phone, Mail, MapPin, Clock, IndianRupee,
         Zap, ChevronRight, X, MessageSquare, Calendar, UserCheck, Ban,
         ArrowRight, MoreHorizontal, Building2, User, BarChart3, FileText, Gauge } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { can } from "@/lib/permissions";
import { formatINR } from "@/lib/utils";
import ProposalModal from "./crm/ProposalModal";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { id: "new_lead",              label: "New Lead",              color: "#6366F1", bg: "#EEF2FF" },
  { id: "contacted",             label: "Contacted",             color: "#8B5CF6", bg: "#F5F3FF", req: "Log an activity first" },
  { id: "site_survey_scheduled", label: "Survey Scheduled",      color: "#F59E0B", bg: "#FFFBEB", req: "Needs phone or email" },
  { id: "site_survey_done",      label: "Survey Done",           color: "#F97316", bg: "#FFF7ED", req: "Add survey note" },
  { id: "proposal_sent",         label: "Proposal Sent",         color: "#3B82F6", bg: "#EFF6FF", req: "Set deal value > 0" },
  { id: "negotiation",           label: "Negotiation",           color: "#EC4899", bg: "#FDF2F8" },
  { id: "won",                   label: "Won",                   color: "#10B981", bg: "#ECFDF5" },
  { id: "lost",                  label: "Lost",                  color: "#EF4444", bg: "#FEF2F2" },
];

const PRIORITIES = { hot: "#EF4444", warm: "#F59E0B", cold: "#3B82F6" };
const SOURCES = ["Website", "Referral", "Cold Call", "Trade Show", "Solar Marketplace", "Government Tender"];
const PROJECT_TYPES = ["Rooftop", "Ground Mount", "Carport", "Floating"];
const FOLLOW_UP_TYPES = [
  { value: "call", label: "Call" },
  { value: "proposal", label: "Send proposal" },
  { value: "site_visit", label: "Site visit" },
  { value: "meeting", label: "Meeting" },
  { value: "other", label: "Other" },
];
const fuLabel = (t) => FOLLOW_UP_TYPES.find(x => x.value === t)?.label || "Follow-up";

// ── Lead Card ──────────────────────────────────────────────────────────────
const LEAD_EDIT_FIELDS = ["contact_name", "company_name", "email", "phone", "address", "city", "state", "pincode",
  "project_type", "capacity_kw", "deal_value", "electricity_bill", "sanctioned_load", "connection_type", "roof_area_sqft",
  "roof_type", "priority", "lead_source", "assigned_to", "assigned_to_name", "notes"];

function LeadCard({ lead, onClick, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("lead_id", lead.id); onDragStart?.(lead); }}
      onClick={() => onClick(lead)}
      data-testid={`lead-card-${lead.id}`}
      style={{ background: "#fff", borderRadius: "10px", padding: "12px", marginBottom: "8px", cursor: "grab",
        border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.15s, transform 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "6px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "#1B2B22", lineHeight: 1.3 }}>{lead.contact_name || "Unnamed"}</div>
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: PRIORITIES[lead.priority] || "#9CA3AF", flexShrink: 0, marginTop: "4px" }} title={lead.priority} />
      </div>
      {lead.company_name && <div style={{ fontSize: "11px", color: "#64748B", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}><Building2 size={10} />{lead.company_name}</div>}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "6px" }}>
        {lead.capacity_kw > 0 && <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "4px", background: "#ECFDF5", color: "#065F46", fontWeight: "600" }}>{lead.capacity_kw} kW</span>}
        {lead.deal_value > 0 && <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "4px", background: "#FEF3C7", color: "#92400E", fontWeight: "600" }}>{formatINR(lead.deal_value)}</span>}
        {lead.lead_source && <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "4px", background: "#F1F5F9", color: "#475569" }}>{lead.lead_source}</span>}
      </div>
      {lead.days_in_stage > 0 && (
        <div style={{ fontSize: "10px", color: lead.days_in_stage > 14 ? "#EF4444" : "#94A3B8", marginTop: "6px", display: "flex", alignItems: "center", gap: "3px" }}>
          <Clock size={9} />{lead.days_in_stage}d in stage
        </div>
      )}
      {lead.next_follow_up_date && lead.stage !== "won" && lead.stage !== "lost" && (() => {
        const t = new Date(); t.setHours(0, 0, 0, 0);
        const fu = new Date(lead.next_follow_up_date); fu.setHours(0, 0, 0, 0);
        const overdue = fu < t, isToday = fu.getTime() === t.getTime();
        const bg = overdue ? "#FEE2E2" : isToday ? "#FEF3C7" : "#EFF6FF";
        const col = overdue ? "#B91C1C" : isToday ? "#92400E" : "#1D4ED8";
        const what = fuLabel(lead.next_follow_up_type || "call");
        const when = overdue ? "overdue" : isToday ? `today${lead.next_follow_up_time ? " " + lead.next_follow_up_time : ""}` : fu.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
        const label = `${what} · ${when}`;
        return (
          <div data-testid={`lead-followup-badge-${lead.id}`} title={lead.next_follow_up_note || ""}
            style={{ marginTop: "6px", display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "10px", padding: "2px 6px", borderRadius: "4px", background: bg, color: col, fontWeight: 700, maxWidth: "100%" }}>
            <Clock size={9} />{label}
            {lead.next_follow_up_note && <span style={{ fontWeight: 500, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>— {lead.next_follow_up_note}</span>}
          </div>
        );
      })()}
    </div>
  );
}

// ── Stage Progress & Checklist ──────────────────────────────────────────────
function StageProgress({ lead, onMoveStage, canEdit = true }) {
  const currentIdx = STAGES.findIndex(s => s.id === lead.stage);
  const currentStage = STAGES[currentIdx];
  const nextStage = currentIdx >= 0 && currentIdx < STAGES.length - 2 ? STAGES[currentIdx + 1] : null;
  const isTerminal = lead.stage === "won" || lead.stage === "lost";

  // Compute checklist for the NEXT stage
  const userActivities = (lead.activities || []).filter(a =>
    ["call", "email", "note", "meeting", "site_visit"].includes(a.type)
  );
  const surveyActivities = (lead.activities || []).filter(a =>
    ["site_visit", "note", "meeting"].includes(a.type)
  );

  const CHECKLIST_MAP = {
    contacted: [
      { label: "Log an activity (call, email, or note)", met: userActivities.length > 0 },
    ],
    site_survey_scheduled: [
      { label: "Phone or email on file", met: !!(lead.phone || lead.email) },
    ],
    site_survey_done: [
      { label: "Survey note or site visit logged", met: surveyActivities.length > 0 },
    ],
    proposal_sent: [
      { label: "Deal value set (> 0)", met: (lead.deal_value || 0) > 0 },
    ],
  };

  const checks = nextStage ? (CHECKLIST_MAP[nextStage.id] || []) : [];
  const allMet = checks.length === 0 || checks.every(c => c.met);
  const [moving, setMoving] = useState(false);

  const handleMove = async () => {
    if (!nextStage || !allMet) return;
    setMoving(true);
    try {
      await onMoveStage(nextStage.id);
    } catch (err) { console.error(err); }
    setMoving(false);
  };

  if (isTerminal) return null;

  return (
    <div style={{ marginBottom: "16px", padding: "14px", borderRadius: "10px", background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 50%, #F0F9FF 100%)", border: "1px solid #D1FAE5" }}
      data-testid="stage-progress">
      {/* Current → Next header */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: currentStage?.color || "#94A3B8" }} />
          <span style={{ fontSize: "11px", fontWeight: "700", color: currentStage?.color || "#64748B" }}>{currentStage?.label}</span>
        </div>
        {nextStage && (
          <>
            <ChevronRight size={14} style={{ color: "#94A3B8" }} />
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: nextStage.color, opacity: 0.5 }} />
              <span style={{ fontSize: "11px", fontWeight: "700", color: nextStage.color }}>{nextStage.label}</span>
            </div>
          </>
        )}
      </div>

      {/* Stage progress bar */}
      <div style={{ display: "flex", gap: "3px", marginBottom: "12px" }}>
        {STAGES.filter(s => s.id !== "lost").map((s, i) => {
          const isCurrent = s.id === lead.stage;
          const isPast = i < currentIdx;
          return (
            <div key={s.id} style={{
              flex: 1, height: "4px", borderRadius: "2px",
              background: isPast ? "#2F7D52" : isCurrent ? s.color : "#E2E8F0",
              transition: "background 0.3s ease",
            }} />
          );
        })}
      </div>

      {/* Checklist */}
      {checks.length > 0 && (
        <div style={{ marginBottom: "10px" }}>
          <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#64748B", marginBottom: "6px", letterSpacing: "0.05em" }}>
            Requirements for {nextStage.label}
          </div>
          {checks.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "7px", padding: "4px 0" }}>
              <div style={{
                width: "16px", height: "16px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: c.met ? "#10B981" : "#F3F4F6", border: c.met ? "none" : "1.5px solid #D1D5DB",
                transition: "all 0.2s ease",
              }}>
                {c.met ? (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  <X size={8} style={{ color: "#9CA3AF" }} />
                )}
              </div>
              <span style={{ fontSize: "11px", color: c.met ? "#065F46" : "#6B7280", fontWeight: c.met ? "600" : "500",
                textDecoration: c.met ? "none" : "none" }}>
                {c.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Move button — leads move freely; checklist above is advisory only */}
      {canEdit && nextStage && (
        <button
          onClick={handleMove}
          disabled={moving}
          data-testid="move-next-stage-btn"
          style={{
            width: "100%", padding: "9px 16px", borderRadius: "8px", border: "none",
            background: nextStage.color,
            color: "#fff",
            fontSize: "12px", fontWeight: "700", cursor: moving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            transition: "all 0.2s ease",
            opacity: moving ? 0.7 : 1,
          }}
        >
          <ArrowRight size={14} />
          {moving ? "Moving..." : `Move to ${nextStage.label}`}
        </button>
      )}
    </div>
  );
}

// ── Detail Field (extracted to prevent focus loss) ─────────────────────────
function DetailField({ label, field, type, editing, form, setForm, displayValue }) {
  return (
    <div style={{ marginBottom: "8px" }}>
      <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", letterSpacing: "0.05em" }}>{label}</label>
      {editing ? (
        <input type={type || "text"} value={form[field] || ""} onChange={e => setForm(p => ({ ...p, [field]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
          style={{ width: "100%", padding: "6px 8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px", marginTop: "2px" }} />
      ) : (
        <div style={{ fontSize: "13px", color: "#1B2B22", marginTop: "2px" }}>{displayValue || "—"}</div>
      )}
    </div>
  );
}

// ── Lead Detail Slide-in Panel ─────────────────────────────────────────────
function LinkedDesigns({ leadId }) {
  const [designs, setDesigns] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/solar-designs?lead_id=${leadId}`).then(r => setDesigns(r.data)).catch(() => {});
  }, [leadId]);
  if (!designs.length) return null;
  return (
    <div style={{ marginBottom: "16px" }} data-testid="lead-linked-designs">
      <div style={{ fontSize: "12px", fontWeight: 800, color: "#1B2B22", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Linked Solar Designs</div>
      {designs.map(d => (
        <Link key={d.id} to={`/app/solar-design?lead_id=${leadId}`} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#2F7D52", padding: "4px 0", textDecoration: "none" }}>
          <Zap size={12} />{d.name || "Untitled Design"}
        </Link>
      ))}
    </div>
  );
}

function LeadDetailPanel({ lead, onClose, onUpdate, onMoveStage, teamMembers, user }) {
  const canEditLead = can(user, "crm.edit");
  const canConvert = can(user, "crm.convert");
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const isARM = (workspace?.name || workspace?.slug || "").toLowerCase().includes("arm");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [noteText, setNoteText] = useState("");
  const [activityType, setActivityType] = useState("note");
  const [showConvert, setShowConvert] = useState(false);
  const [showProposal, setShowProposal] = useState(false);

  useEffect(() => {
    // Refreshes (activity added, follow-up set) must not wipe an edit in progress
    if (lead && !editing) setForm({ ...lead });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead]);

  if (!lead) return null;

  const saveEdits = async () => {
    try {
      // Send only what the form edits — never the whole lead (activities, stage
      // history, conversion link…) which the server now ignores anyway.
      const patch = {};
      LEAD_EDIT_FIELDS.forEach(k => { if (form[k] !== undefined && form[k] !== lead[k]) patch[k] = form[k]; });
      if (Object.keys(patch).length) await onUpdate(lead.id, patch);
      setEditing(false);
      toast.success("Lead updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const addActivity = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/api/crm/leads/${lead.id}/activities`, { type: activityType, description: noteText });
      setNoteText("");
      onUpdate(lead.id, {}); // refresh
      toast.success("Activity added");
    } catch { toast.error("Failed to add activity"); }
  };

  const markLost = async () => {
    const reason = window.prompt("Reason for losing this lead?", "Price too high");
    if (!reason) return;
    try {
      await onUpdate(lead.id, { stage: "lost", lost_reason: reason });
      toast.info("Lead marked as lost");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark the lead as lost"); }
  };

  const convertToProject = () => setShowConvert(true);

  return (
    <div style={{ position: "fixed", top: 0, right: 0, width: "min(420px, 100vw)", height: "100vh", background: "#fff",
      boxShadow: "-4px 0 24px rgba(0,0,0,0.12)", zIndex: 1000, display: "flex", flexDirection: "column",
      fontFamily: "Manrope, sans-serif", overflow: "hidden" }} data-testid="lead-detail-panel">
      {/* Header */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "16px", fontWeight: "800", color: "#1B2B22" }}>{lead.contact_name}</div>
          <div style={{ fontSize: "12px", color: "#64748B" }}>{lead.company_name}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}><X size={20} /></button>
      </div>
      {showProposal && (
        <ProposalModal lead={lead} onClose={() => setShowProposal(false)} onGenerated={() => onUpdate(lead.id, {})} />
      )}
      {showConvert && (
        <ConvertLeadModal lead={lead} onClose={() => setShowConvert(false)}
          onConverted={(data) => { setShowConvert(false); onUpdate(lead.id, {}); navigate(`/app/projects/${data.project_id}`); }} />
      )}

      {/* Body (scrollable) */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {/* Quick actions */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
          {canEditLead && <button onClick={() => { if (editing) setForm({ ...lead }); setEditing(!editing); }} style={{ padding: "5px 10px", borderRadius: "6px", border: "1px solid #D1D5DB", background: editing ? "#DBEAFE" : "#fff", fontSize: "11px", cursor: "pointer", fontWeight: "600" }}>
            {editing ? "Cancel Edit" : "Edit"}
          </button>}
          {editing && <button onClick={saveEdits} style={{ padding: "5px 10px", borderRadius: "6px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "11px", cursor: "pointer", fontWeight: "600" }}>Save</button>}
          {canConvert && lead.stage === "won" && !lead.linked_project_id && (
            <button onClick={convertToProject} style={{ padding: "5px 10px", borderRadius: "6px", border: "none", background: "#2563EB", color: "#fff", fontSize: "11px", cursor: "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
              <ArrowRight size={11} />Convert to Project
            </button>
          )}
          {lead.linked_project_id && <span style={{ padding: "5px 10px", borderRadius: "6px", background: "#ECFDF5", fontSize: "11px", color: "#065F46", fontWeight: "600" }}>Project Linked</span>}
          {canEditLead && lead.stage !== "lost" && lead.stage !== "won" && (
            <button onClick={markLost} style={{ padding: "5px 10px", borderRadius: "6px", border: "1px solid #FCA5A5", background: "#FEF2F2", color: "#DC2626", fontSize: "11px", cursor: "pointer", fontWeight: "600" }}>
              <Ban size={10} style={{ marginRight: "3px", display: "inline" }} />Mark Lost
            </button>
          )}
        </div>

        {/* Stage Progress & Checklist */}
        <StageProgress lead={lead} onMoveStage={onMoveStage} canEdit={canEditLead} />

        {/* Lead action bar: design, DPR, ARM offer */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", margin: "12px 0" }}>
          <button onClick={() => navigate(`/app/solar-design?lead_id=${lead.id}`)} data-testid="lead-design-btn"
            style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", borderRadius: "8px", border: "1px solid #2F7D52", background: "#ECFDF5", color: "#065F46", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
            <Zap size={13} />Design System
          </button>
          {canEditLead && (
            <button onClick={() => setShowProposal(true)} data-testid="lead-proposal-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", borderRadius: "8px", border: "none", background: "#0A1128", color: "#FFB703", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
              <FileText size={13} />Proposal
            </button>
          )}
          <button onClick={() => navigate(`/app/crm/leads/${lead.id}/report`)} data-testid="lead-dpr-btn"
            style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", borderRadius: "8px", border: "1px solid #FFB703", background: "#FFF8E7", color: "#92700A", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
            <FileText size={13} />Project Report (DPR)
          </button>
          {can(user, "bill_audit.view") && (
            <button onClick={() => navigate(`/app/crm/leads/${lead.id}/bill-audit`)} data-testid="lead-bill-audit-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", borderRadius: "8px", border: "1px solid #0A1128", background: "#EEF2FF", color: "#0A1128", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
              <Gauge size={13} />Bill Audit
            </button>
          )}
          {isARM && (
            <button onClick={() => {
              const loc = [lead.city, lead.state].filter(Boolean).join(", ");
              const params = new URLSearchParams();
              if (lead.contact_name || lead.company_name) params.set("client", lead.company_name || lead.contact_name);
              if (loc) params.set("loc", loc);
              if (lead.capacity_kw) params.set("capacity", lead.capacity_kw);
              if (lead.phone) params.set("phone", lead.phone);
              if (lead.email) params.set("email", lead.email);
              window.open(`${process.env.REACT_APP_BACKEND_URL}/arm-proposal-generator.html?${params.toString()}`, "_blank");
            }} data-testid="lead-offer-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", borderRadius: "8px", border: "none", background: "#0A1128", color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
              <FileText size={13} />Generate Offer
            </button>
          )}
        </div>

        {/* Linked solar designs */}
        <LinkedDesigns leadId={lead.id} />


        {/* Contact Info */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "12px", fontWeight: "800", color: "#1B2B22", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Contact</div>
          <DetailField label="Name" field="contact_name" editing={editing} form={form} setForm={setForm} displayValue={lead.contact_name} />
          <DetailField label="Company" field="company_name" editing={editing} form={form} setForm={setForm} displayValue={lead.company_name} />
          <DetailField label="Email" field="email" editing={editing} form={form} setForm={setForm} displayValue={lead.email} />
          <DetailField label="Phone" field="phone" editing={editing} form={form} setForm={setForm} displayValue={lead.phone} />
          <DetailField label="City" field="city" editing={editing} form={form} setForm={setForm} displayValue={lead.city} />
          <DetailField label="State" field="state" editing={editing} form={form} setForm={setForm} displayValue={lead.state} />
          <DetailField label="Address" field="address" editing={editing} form={form} setForm={setForm} displayValue={lead.address} />
        </div>

        {/* Project Details */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "12px", fontWeight: "800", color: "#1B2B22", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Project Details</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
            <DetailField label="Type" field="project_type" editing={editing} form={form} setForm={setForm} displayValue={lead.project_type} />
            <DetailField label="Capacity (kW)" field="capacity_kw" type="number" editing={editing} form={form} setForm={setForm} displayValue={lead.capacity_kw} />
            <DetailField label="Deal Value (INR)" field="deal_value" type="number" editing={editing} form={form} setForm={setForm} displayValue={lead.deal_value} />
            <DetailField label="Elec. Bill" field="electricity_bill" type="number" editing={editing} form={form} setForm={setForm} displayValue={lead.electricity_bill} />
            <DetailField label="Roof Area (sqft)" field="roof_area_sqft" type="number" editing={editing} form={form} setForm={setForm} displayValue={lead.roof_area_sqft} />
            <DetailField label="Roof Type" field="roof_type" editing={editing} form={form} setForm={setForm} displayValue={lead.roof_type} />
          </div>
        </div>

        {/* Assignment */}
        {editing && (
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496" }}>Assign To</label>
            <select value={form.assigned_to || ""} onChange={e => {
              const member = teamMembers.find(m => m.id === e.target.value);
              setForm(p => ({ ...p, assigned_to: e.target.value, assigned_to_name: member?.name || "" }));
            }} style={{ width: "100%", padding: "6px 8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px", marginTop: "2px" }}>
              <option value="">Unassigned</option>
              {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        )}

        {/* Next Follow-up */}
        <FollowUpCard lead={lead} onChanged={() => onUpdate(lead.id, {})} canEdit={canEditLead} />

        {/* Notes */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontSize: "12px", fontWeight: "800", color: "#1B2B22", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Notes</div>
          {editing ? (
            <textarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3}
              style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px", resize: "vertical" }} />
          ) : (
            <div style={{ fontSize: "12px", color: "#475569", whiteSpace: "pre-wrap" }}>{lead.notes || "No notes"}</div>
          )}
        </div>

        {/* Add Activity */}
        {canEditLead && <div style={{ marginBottom: "16px", padding: "12px", background: "#F5F8F5", borderRadius: "8px" }}>
          <div style={{ fontSize: "12px", fontWeight: "800", color: "#1B2B22", marginBottom: "8px" }}>Add Activity</div>
          <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
            {["note", "call", "email", "site_visit", "meeting"].map(t => (
              <button key={t} onClick={() => setActivityType(t)}
                style={{ padding: "3px 8px", borderRadius: "5px", border: activityType === t ? "1px solid #2F7D52" : "1px solid #D1D5DB",
                  background: activityType === t ? "#E6F4EC" : "#fff", fontSize: "10px", cursor: "pointer", textTransform: "capitalize" }}>
                {t.replace("_", " ")}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note or log activity..."
              onKeyDown={e => e.key === "Enter" && addActivity()}
              style={{ flex: 1, padding: "7px 10px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px" }} />
            <button onClick={addActivity} style={{ padding: "7px 12px", borderRadius: "6px", background: "#2F7D52", color: "#fff", border: "none", fontSize: "11px", cursor: "pointer", fontWeight: "600" }}>Add</button>
          </div>
        </div>}

        {/* Activity Timeline */}
        {lead.activities?.length > 0 && (
          <div>
            <div style={{ fontSize: "12px", fontWeight: "800", color: "#1B2B22", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Timeline</div>
            {lead.activities.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "8px", paddingLeft: "12px", borderLeft: "2px solid #E5E7EB" }}>
                <div>
                  <div style={{ fontSize: "11px", color: "#1B2B22" }}>{a.description}</div>
                  <div style={{ fontSize: "10px", color: "#94A3B8" }}>{a.type} · {new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Lost Reason */}
        {lead.stage === "lost" && lead.lost_reason && (
          <div style={{ padding: "10px", background: "#FEF2F2", borderRadius: "8px", marginTop: "12px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "#DC2626" }}>Lost Reason</div>
            <div style={{ fontSize: "12px", color: "#7F1D1D" }}>{lead.lost_reason}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Form Input (extracted to prevent focus loss) ───────────────────────────
function FormInput({ label, field, type, half, form, setForm }) {
  return (
    <div style={{ flex: half ? "1 1 45%" : "1 1 100%", marginBottom: "8px" }}>
      <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" }}>{label}</label>
      <input type={type || "text"} value={form[field] || ""} onChange={e => setForm(p => ({ ...p, [field]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
        style={{ width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px" }} />
    </div>
  );
}

// ── New Lead Modal ──────────────────────────────────────────────────────────
function FollowUpCard({ lead, onChanged, canEdit = true }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
  const [form, setForm] = useState({ date: tomorrow(), time: "09:00", type: "call", note: "" });
  const closed = lead.stage === "won" || lead.stage === "lost";
  const has = !!lead.next_follow_up_date;

  const openEditor = () => {
    setForm({ date: lead.next_follow_up_date || tomorrow(), time: lead.next_follow_up_time || "09:00", type: lead.next_follow_up_type || "call", note: lead.next_follow_up_note || "" });
    setOpen(true);
  };
  const save = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/crm/leads/${lead.id}/follow-up`, form);
      toast.success(`${fuLabel(form.type)} scheduled — you'll be reminded at ${form.time || "09:00"} on ${new Date(form.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`);
      setOpen(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save follow-up"); }
    setSaving(false);
  };
  const done = async () => {
    const outcome = window.prompt(`Mark "${fuLabel(lead.next_follow_up_type)}" as done. Outcome (optional):`, "");
    if (outcome === null) return;
    try {
      await axios.post(`${API}/api/crm/leads/${lead.id}/follow-up/done`, { outcome });
      toast.success("Logged to activity");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fu = has ? new Date(lead.next_follow_up_date) : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = fu && fu < today;
  const inp = { width: "100%", padding: "6px 8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px" };
  const btn = { padding: "5px 10px", borderRadius: "6px", border: "1px solid #D1D5DB", background: "#fff", fontSize: "11px", cursor: "pointer", fontWeight: "600" };

  return (
    <div style={{ marginBottom: "16px" }} data-testid="lead-followup-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <div style={{ fontSize: "12px", fontWeight: "800", color: "#1B2B22", textTransform: "uppercase", letterSpacing: "0.05em" }}>Next Follow-up</div>
        {canEdit && !closed && !open && (
          <div style={{ display: "flex", gap: "6px" }}>
            {has && <button onClick={done} data-testid="lead-followup-done" style={{ ...btn, background: "#ECFDF5", borderColor: "#A7F3D0", color: "#065F46" }}>✓ Done</button>}
            <button onClick={openEditor} data-testid="lead-followup-set" style={{ ...btn, background: "#2F7D52", borderColor: "#2F7D52", color: "#fff" }}>
              {has ? "Change" : "+ Set follow-up"}
            </button>
          </div>
        )}
      </div>
      {open ? (
        <div style={{ border: "1px solid #D1FAE5", background: "#F0FDF4", borderRadius: "8px", padding: "10px" }}>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} style={inp} data-testid="lead-followup-date-input" />
            <input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} style={{ ...inp, width: "110px", flex: "0 0 auto" }} title="Reminder time (IST)" data-testid="lead-followup-time-input" />
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={inp} data-testid="lead-followup-type">
              {FOLLOW_UP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <input value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} maxLength={500}
            placeholder="What to do — e.g. call about revised BOQ, share proposal v2, site visit with structural team"
            style={{ ...inp, marginBottom: "8px" }} data-testid="lead-followup-note" onKeyDown={e => e.key === "Enter" && save()} />
          <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
            <button onClick={() => setOpen(false)} style={btn}>Cancel</button>
            <button onClick={save} disabled={saving} data-testid="lead-followup-save" style={{ ...btn, background: "#2F7D52", borderColor: "#2F7D52", color: "#fff" }}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      ) : has ? (
        <div style={{ fontSize: "12px", color: overdue ? "#B91C1C" : "#2F7D52" }} data-testid="lead-followup-date">
          <span style={{ fontWeight: 800 }}>{fuLabel(lead.next_follow_up_type || "call")}</span>
          {" · "}{fu.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{lead.next_follow_up_time ? ` at ${lead.next_follow_up_time}` : ""}{overdue ? " (overdue)" : ""}
          {lead.next_follow_up_note && <div style={{ color: "#475569", marginTop: "3px", fontWeight: 500 }}>{lead.next_follow_up_note}</div>}
        </div>
      ) : (
        <div style={{ fontSize: "12px", color: "#94A3B8" }} data-testid="lead-followup-date">
          {closed ? "—" : "Not set — the owner gets an email + bell reminder at the chosen date and time (IST)"}
        </div>
      )}
    </div>
  );
}


const PROJECT_TEMPLATE_TYPES = [
  { value: "rooftop", label: "Rooftop (template: 8 phases)" },
  { value: "ground_mount", label: "Ground Mount (template)" },
  { value: "ground_mount_oa", label: "Ground Mount – Open Access (template)" },
  { value: "blank", label: "Blank (no template tasks)" },
];

function ConvertLeadModal({ lead, onClose, onConverted }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/crm/leads/${lead.id}/convert-preview`)
      .then(({ data }) => setForm({ ...data.defaults, expected_cod: "" }))
      .catch(() => { toast.error("Could not load lead details"); onClose(); });
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Project name is required"); return; }
    if (!form.location?.trim()) { toast.error("Location is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.expected_cod) delete payload.expected_cod;
      const { data } = await axios.post(`${API}/api/crm/leads/${lead.id}/convert`, payload);
      toast.success(`Project created with ${data.tasks_created} template tasks`);
      onConverted(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Conversion failed"); }
    setSaving(false);
  };

  const inp = { width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px" };
  const lbl = { fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "min(520px, calc(100vw - 24px))", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 16px 48px rgba(0,0,0,0.15)", fontFamily: "Manrope, sans-serif" }}
        data-testid="convert-lead-modal">
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "#1B2B22" }}>Convert to Project</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: "12px", color: "#64748B" }}>
          Pre-filled from the lead. The project gets the full task template for its type; set an expected COD so the tasks are scheduled automatically.
        </p>
        {!form ? <div style={{ fontSize: "12px", color: "#94A3B8" }}>Loading…</div> : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
            <div style={{ flex: "1 1 100%", marginBottom: "8px" }}>
              <label style={lbl}>Project Name *</label>
              <input value={form.name || ""} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inp} data-testid="convert-name" />
            </div>
            <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
              <label style={lbl}>Template / Project Type</label>
              <select value={form.project_type} onChange={e => setForm(p => ({ ...p, project_type: e.target.value }))} style={inp} data-testid="convert-type">
                {PROJECT_TEMPLATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
              <label style={lbl}>Expected COD</label>
              <input type="date" value={form.expected_cod || ""} onChange={e => setForm(p => ({ ...p, expected_cod: e.target.value }))} style={inp} data-testid="convert-cod" />
            </div>
            <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
              <label style={lbl}>Capacity (kWp)</label>
              <input type="number" value={form.capacity_kwp ?? ""} onChange={e => setForm(p => ({ ...p, capacity_kwp: Number(e.target.value) }))} style={inp} />
            </div>
            <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
              <label style={lbl}>Contract Value (INR)</label>
              <input type="number" value={form.contract_value ?? ""} onChange={e => setForm(p => ({ ...p, contract_value: Number(e.target.value) }))} style={inp} />
            </div>
            <div style={{ flex: "1 1 100%", marginBottom: "8px" }}>
              <label style={lbl}>Location *</label>
              <input value={form.location || ""} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} style={inp} />
            </div>
            <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
              <label style={lbl}>Client Name</label>
              <input value={form.client_name || ""} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} style={inp} />
            </div>
            <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
              <label style={lbl}>Client Email</label>
              <input value={form.client_email || ""} onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))} style={inp} />
            </div>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
          <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: "7px", border: "1px solid #D1D5DB", background: "#fff", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} disabled={saving || !form} data-testid="convert-submit"
            style={{ padding: "8px 14px", borderRadius: "7px", border: "none", background: "#2563EB", color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}


function NewLeadModal({ onClose, onCreated, teamMembers }) {
  const [form, setForm] = useState({ project_type: "Rooftop", priority: "warm", lead_source: "Website" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.contact_name?.trim()) { toast.error("Contact name is required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/crm/leads`, form);
      toast.success("Lead created");
      onCreated(data);
      onClose();
    } catch { toast.error("Failed to create lead"); }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "min(520px, calc(100vw - 24px))", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 16px 48px rgba(0,0,0,0.15)" }}
        data-testid="new-lead-modal">
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "#1B2B22" }}>New Lead</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0 12px" }}>
          <FormInput label="Contact Name *" field="contact_name" form={form} setForm={setForm} />
          <FormInput label="Company" field="company_name" form={form} setForm={setForm} />
          <FormInput label="Email" field="email" half form={form} setForm={setForm} />
          <FormInput label="Phone" field="phone" half form={form} setForm={setForm} />
          <FormInput label="City" field="city" half form={form} setForm={setForm} />
          <FormInput label="State" field="state" half form={form} setForm={setForm} />
          <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" }}>Project Type</label>
            <select value={form.project_type} onChange={e => setForm(p => ({ ...p, project_type: e.target.value }))}
              style={{ width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px" }}>
              {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <FormInput label="Capacity (kW)" field="capacity_kw" type="number" half form={form} setForm={setForm} />
          <FormInput label="Deal Value (INR)" field="deal_value" type="number" half form={form} setForm={setForm} />
          <FormInput label="Monthly Elec. Bill" field="electricity_bill" type="number" half form={form} setForm={setForm} />
          <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" }}>Lead Source</label>
            <select value={form.lead_source} onChange={e => setForm(p => ({ ...p, lead_source: e.target.value }))}
              style={{ width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px" }}>
              {SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ flex: "1 1 45%", marginBottom: "8px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" }}>Priority</label>
            <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
              style={{ width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px" }}>
              <option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
            </select>
          </div>
          <div style={{ flex: "1 1 100%", marginBottom: "8px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" }}>Assign To</label>
            <select value={form.assigned_to || ""} onChange={e => {
              const member = teamMembers.find(m => m.id === e.target.value);
              setForm(p => ({ ...p, assigned_to: e.target.value, assigned_to_name: member?.name || "" }));
            }} style={{ width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px" }}>
              <option value="">Auto (me)</option>
              {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div style={{ flex: "1 1 100%", marginBottom: "8px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "2px" }}>Notes</label>
            <textarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
              style={{ width: "100%", padding: "7px 10px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px", resize: "vertical" }} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #D1D5DB", background: "#fff", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ padding: "8px 20px", borderRadius: "8px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "12px", cursor: "pointer", fontWeight: "700" }}
            data-testid="create-lead-btn">
            {saving ? "Creating..." : "Create Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Main CRM Pipeline Page ──────────────────────────────────────────────────
function ReminderStatusButton() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/crm/reminder-status`); setStatus(data); } catch { setStatus(null); }
  };
  useEffect(() => { load(); }, []);
  const sendNow = async () => {
    if (!window.confirm(`Send today's pending follow-up reminders now? ${status?.reminders_pending_today ?? 0} of ${status?.leads_due_today ?? 0} due today have not been sent yet.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/crm/run-followup-reminders`);
      toast.success(`${data.leads_due} lead(s) due, ${data.emails_sent} email(s) sent`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const ok = status?.scheduler_running && status?.job_scheduled;
  const last = status?.last_run?.ran_at ? new Date(status.last_run.ran_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never";
  return (
    <button onClick={sendNow} disabled={busy} data-testid="reminder-status-btn"
      title={status ? `Reminder poller ${ok ? "running (checks every minute)" : "NOT running"} · last check: ${status.last_poll_at ? new Date(status.last_poll_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"} · last send: ${last} (${status.last_run?.emails_sent ?? 0} emails) · ${status.reminders_pending_today ?? 0} still to send today · email ${status.email_configured ? "configured" : "NOT configured"} · click to send today's pending reminders now` : "Reminder status unavailable"}
      style={{ padding: "7px 12px", borderRadius: "8px", border: `1px solid ${status ? (ok ? "#A7F3D0" : "#FCA5A5") : "#E5E7EB"}`, background: "#fff", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: status ? (ok ? "#065F46" : "#B91C1C") : "#64748B" }}>
      <Clock size={12} />{status ? `Reminders ${ok ? "on" : "OFF"} · ${status.leads_due_today} due` : "Reminders"}
    </button>
  );
}


export default function CRMPipelinePage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewLead, setShowNewLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [filterSource, setFilterSource] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const fileInputRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/crm/leads`);
      setLeads(data);
    } catch (err) { console.error("Failed to fetch leads", err); }
    setLoading(false);
  }, []);

  const fetchTeam = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/crm/team`); setTeamMembers(data); } catch (err) { console.error("Failed to fetch team", err); }
  }, []);

  useEffect(() => { fetchLeads(); fetchTeam(); }, [fetchLeads, fetchTeam]);

  const updateLead = async (leadId, updates) => {
    try {
      await axios.put(`${API}/api/crm/leads/${leadId}`, updates);
      // Refresh the specific lead and list
      const { data: updatedLead } = await axios.get(`${API}/api/crm/leads/${leadId}`);
      setSelectedLead(updatedLead);
      fetchLeads();
    } catch (err) { throw err; }
  };

  const handleDrop = async (stageId, e) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("lead_id");
    if (!leadId) return;
    if (!can(user, "crm.edit")) { toast.error("Your role can view the pipeline but not move leads"); return; }
    try {
      await axios.put(`${API}/api/crm/leads/${leadId}/stage`, { stage: stageId });
      fetchLeads();
      toast.success(`Moved to ${STAGES.find(s => s.id === stageId)?.label}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail) {
        toast.error(detail);
      } else {
        toast.error("Failed to move lead");
      }
    }
  };

  const moveLeadToStage = async (stageId) => {
    if (!selectedLead) return;
    try {
      await axios.put(`${API}/api/crm/leads/${selectedLead.id}/stage`, { stage: stageId });
      toast.success(`Moved to ${STAGES.find(s => s.id === stageId)?.label}`);
      // Refresh lead detail and list
      const { data: updatedLead } = await axios.get(`${API}/api/crm/leads/${selectedLead.id}`);
      setSelectedLead(updatedLead);
      fetchLeads();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail) {
        toast.error(detail);
      } else {
        toast.error("Failed to move lead");
      }
      throw err;
    }
  };

  const openLead = async (lead) => {
    try {
      const { data } = await axios.get(`${API}/api/crm/leads/${lead.id}`);
      setSelectedLead(data);
    } catch { setSelectedLead(lead); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/crm/import-csv`, formData);
      toast.success(data.message);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || "CSV import failed"); }
    e.target.value = "";
  };

  // Filter leads (memoized)
  const filtered = useMemo(() => leads.filter(l => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!((l.contact_name || "").toLowerCase().includes(q) || (l.company_name || "").toLowerCase().includes(q) || (l.city || "").toLowerCase().includes(q))) return false;
    }
    if (filterSource && l.lead_source !== filterSource) return false;
    if (filterPriority && l.priority !== filterPriority) return false;
    return true;
  }), [leads, searchQuery, filterSource, filterPriority]);

  // Group by stage (memoized)
  const byStage = useMemo(() => {
    const groups = {};
    STAGES.forEach(s => { groups[s.id] = filtered.filter(l => l.stage === s.id); });
    return groups;
  }, [filtered]);

  // Pipeline value (memoized)
  const pipelineValue = useMemo(() =>
    filtered.filter(l => !["won", "lost"].includes(l.stage)).reduce((s, l) => s + (l.deal_value || 0), 0),
  [filtered]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", fontFamily: "Manrope, sans-serif" }} data-testid="crm-pipeline-page">
      {/* Header */}
      <div style={{ padding: "16px 24px", borderBottom: "1px solid #E5E7EB", background: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#1B2B22" }}>CRM Pipeline</h1>
          <div style={{ fontSize: "12px", color: "#64748B", marginTop: "2px" }}>
            {leads.length} leads · Pipeline: <span style={{ fontWeight: "700", color: "#2F7D52" }}>{formatINR(pipelineValue, { zero: "₹0" })}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: "8px", top: "8px", color: "#94A3B8" }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search leads..."
              style={{ paddingLeft: "28px", padding: "7px 10px 7px 28px", borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "12px", width: "200px" }}
              data-testid="crm-search" />
          </div>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "11px", color: "#64748B" }}>
            <option value="">All Sources</option>
            {SOURCES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "11px", color: "#64748B" }}>
            <option value="">All Priority</option>
            <option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
          </select>
          <input type="file" accept=".csv" ref={fileInputRef} style={{ display: "none" }} onChange={handleCSVImport} />
          <Link to="/app/crm-reports" data-testid="crm-reports-link"
            style={{ padding: "7px 12px", borderRadius: "8px", border: "1px solid #E5E7EB", background: "#fff", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: "#64748B", textDecoration: "none" }}>
            <BarChart3 size={12} />Analytics
          </Link>
          {can(user, "crm.edit") && <button onClick={() => fileInputRef.current?.click()} style={{ padding: "7px 12px", borderRadius: "8px", border: "1px solid #E5E7EB", background: "#fff", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", color: "#64748B" }}>
            <Upload size={12} />Import CSV
          </button>}
          {can(user, "crm.reminders") && (
            <ReminderStatusButton />
          )}
          {can(user, "crm.edit") && <button onClick={() => setShowNewLead(true)} data-testid="new-lead-btn"
            style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "12px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "5px" }}>
            <Plus size={14} />New Lead
          </button>}
        </div>
      </div>

      {/* Kanban Board */}
      <div style={{ flex: 1, overflowX: "auto", padding: "16px", display: "flex", gap: "12px" }}>
        {STAGES.map(stage => (
          <div key={stage.id}
            onDragOver={e => e.preventDefault()}
            onDrop={e => handleDrop(stage.id, e)}
            data-testid={`stage-column-${stage.id}`}
            style={{ minWidth: "240px", maxWidth: "280px", flex: "1 0 240px", display: "flex", flexDirection: "column", background: stage.bg, borderRadius: "12px", padding: "12px", border: `1px solid ${stage.color}22` }}>
            {/* Stage header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: stage.color }} />
                <span style={{ fontSize: "12px", fontWeight: "800", color: stage.color }}>{stage.label}</span>
              </div>
              <span style={{ fontSize: "11px", fontWeight: "700", color: stage.color, background: `${stage.color}15`, padding: "2px 8px", borderRadius: "10px" }}>
                {byStage[stage.id]?.length || 0}
              </span>
            </div>
            {stage.req && (
              <div style={{ fontSize: "9px", color: "#94A3B8", marginBottom: "8px", fontStyle: "italic" }}>
                {stage.req}
              </div>
            )}
            {!stage.req && <div style={{ marginBottom: "8px" }} />}
            {/* Lead cards */}
            <div style={{ flex: 1, overflowY: "auto", minHeight: "100px" }}>
              {byStage[stage.id]?.map(lead => (
                <LeadCard key={lead.id} lead={lead} onClick={openLead} />
              ))}
              {byStage[stage.id]?.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px 10px", fontSize: "11px", color: "#94A3B8" }}>
                  Drop leads here
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {showNewLead && <NewLeadModal onClose={() => setShowNewLead(false)} onCreated={() => fetchLeads()} teamMembers={teamMembers} />}
      {selectedLead && <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdate={updateLead} onMoveStage={moveLeadToStage} teamMembers={teamMembers} user={user} />}
    </div>
  );
}
